import { Component, OnInit, signal, Output, EventEmitter, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ContactService } from '../../../services/contact.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { SecurityService } from '../../../services/security.service';
import { EncryptionService } from '../../../services/encryption.service';

export interface Message {
  id: string;
  name: string;
  email: string;
  country_code: string;
  phone: string;
  subject: string;
  message: string;
  created_at: string;
  is_read: boolean;
  is_answered: boolean;
  admin_notes?: string;
}

@Component({
  selector: 'app-admin-messages',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-messages.component.html',
  styleUrl: './admin-messages.component.css'
})
export class AdminMessagesComponent implements OnInit {
  messages = signal<Message[]>([]);
  loading = signal(false);
  selectedMessage = signal<Message | null>(null);
  showDetailsModal = signal(false);
  deleting = signal(false);
  adminNotes = '';
  
  // Filter and search
  searchQuery = signal('');
  filterStatus = signal<'all' | 'answered' | 'unanswered'>('all');
  
  @Output() backClicked = new EventEmitter<void>();

  // Computed statistics
  get unansweredCount(): number {
    return this.messages().filter(m => !m.is_answered).length;
  }

  get answeredCount(): number {
    return this.messages().filter(m => m.is_answered).length;
  }

  get todayCount(): number {
    const today = new Date().toDateString();
    return this.messages().filter(m => new Date(m.created_at).toDateString() === today).length;
  }

  // Filtered messages based on search and status filter
  get filteredMessages(): Message[] {
    let filtered = this.messages();
    
    // Apply status filter
    if (this.filterStatus() === 'answered') {
      filtered = filtered.filter(m => m.is_answered);
    } else if (this.filterStatus() === 'unanswered') {
      filtered = filtered.filter(m => !m.is_answered);
    }
    
    // Apply search filter
    const query = this.searchQuery().toLowerCase().trim();
    if (query) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.email.toLowerCase().includes(query) ||
        m.subject.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }

  // @ts-ignore
  constructor(
    private contactService: ContactService,
    private toastService: ToastService,
    private router: Router,
    private securityService: SecurityService,
    private encryptionService: EncryptionService
  ) {}

  ngOnInit() {
    this.loadMessages();
  }

  async loadMessages() {
    this.loading.set(true);
    try {
      const result = await (this.contactService as any).getContactMessages();
      
      // Decrypt sensitive fields for admin viewing
      const decryptedMessages = result.map((msg: Message) => {
        try {
          return this.encryptionService.decryptObject(msg, ['email', 'phone', 'message']);
        } catch (decryptError) {
          console.error('Failed to decrypt message', msg.id, decryptError);
          // Return original if decryption fails (for backward compatibility with old data)
          return msg;
        }
      });
      
      this.messages.set(decryptedMessages || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load messages';
      this.toastService.error(message);
    } finally {
      this.loading.set(false);
    }
  }

  openDetails(message: Message) {
    this.selectedMessage.set(message);
    this.adminNotes = message.admin_notes || '';
    this.showDetailsModal.set(true);
  }

  closeModal() {
    this.showDetailsModal.set(false);
    this.selectedMessage.set(null);
    this.adminNotes = '';
  }

  /**
   * Get escaped HTML version of message field for safe display
   */
  getEscapedField(field: string): string {
    return this.securityService.escapeHtml(field);
  }

  async toggleMessageStatus(message: Message) {
    try {
      // Toggle answered status
      await (this.contactService as any).markAsAnswered(message.id);
      this.messages.update(msgs =>
        msgs.map(m => m.id === message.id ? { ...m, is_answered: !m.is_answered } : m)
      );
      const status = !message.is_answered ? 'marked as answered' : 'marked as unanswered';
      this.toastService.success(`Message ${status}`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to update status';
      this.toastService.error(errorMsg);
    }
  }

  async saveAdminNotes() {
    const message = this.selectedMessage();
    if (!message) return;

    // Sanitize admin notes before saving
    const sanitizedNotes = this.securityService.sanitizeFormInput(this.adminNotes, 5000);

    try {
      await (this.contactService as any).updateAdminNotes(message.id, sanitizedNotes);
      this.messages.update(msgs =>
        msgs.map(m => m.id === message.id ? { ...m, admin_notes: sanitizedNotes } : m)
      );
      this.adminNotes = sanitizedNotes;
      this.toastService.success('Admin notes saved');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to save notes';
      this.toastService.error(errorMsg);
    }
  }

  async deleteMessage(message: Message) {
    if (!confirm(`Delete message from ${message.name}? This action cannot be undone.`)) {
      return;
    }

    this.deleting.set(true);
    try {
      await (this.contactService as any).deleteMessage(message.id);
      this.messages.update(msgs => msgs.filter(m => m.id !== message.id));
      this.toastService.success('Message deleted successfully');
      if (this.selectedMessage()?.id === message.id) {
        this.closeModal();
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete message';
      this.toastService.error(errorMsg);
    } finally {
      this.deleting.set(false);
    }
  }

  goBack() {
    this.router.navigate(['/admin']);
  }

  /**
   * Get initials from name for avatar display
   */
  getInitials(name: string): string {
    const cleaned = this.securityService.escapeHtml(name);
    const words = cleaned.trim().split(/\s+/);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }
}
